import { useLocation } from 'preact-iso'
import { useUserProfile } from './useUserProfile'

export interface WorkspaceCapabilities {
  canAdmin: boolean
  canEdit: boolean
  canView: boolean
  /**
   * Whether the user may export analytics/visitor data (ORB-373). The API gates the
   * export endpoints on a dedicated export capability; until that flag is surfaced on
   * the role-set payload, the dashboard maps it to editor capability (editors/admins can
   * export, viewers cannot). The server remains the source of truth — a 403 is handled
   * globally — so this only controls whether the export affordance is shown.
   */
  canExport: boolean
}

/**
 * Returns the current user's capabilities for the active workspace.
 *
 * This mirrors the API's `[RequireWorkspaceCapability]` checks one-to-one:
 *   - `canEdit`  ≡ `IsWorkspaceEditor` (company admin/editor OR workspace admin/editor)
 *   - `canAdmin` ≡ `IsWorkspaceAdmin` (company admin OR workspace admin)
 *   - `canView`  ≡ `IsWorkspaceViewer`
 * so gating UI on these flags matches what the server will actually allow.
 *
 * By default the company and workspace are read from the current route
 * (`/company/:companyId/workspace/:workspaceId/...`), which lets components gate
 * edit affordances without prop-drilling. Pass explicit ids to override when the
 * relevant workspace is not the one in the URL (e.g. company-level Settings pages).
 *
 * Returns all-false when no matching role set is found (unknown/empty ids), so it
 * is safe to call outside a workspace route.
 */
export function useWorkspaceCapabilities(
  companyIdOverride?: string,
  workspaceIdOverride?: string
): WorkspaceCapabilities {
  const location = useLocation()
  const { getWorkspaceCapabilities } = useUserProfile()

  const path = location?.path ?? ''
  const companyId = companyIdOverride ?? path.match(/\/company\/([^/]+)/)?.[1] ?? ''
  const workspaceId = workspaceIdOverride ?? path.match(/\/workspace\/([^/]+)/)?.[1] ?? ''

  const caps = getWorkspaceCapabilities(companyId, workspaceId)
  return { ...caps, canExport: caps.canEdit }
}
